import { lazy, Suspense, useEffect, useState } from "react";
import "./App.css";
import ReactGA from "react-ga4";
import Header from "./components/Header/Header";
import HeroScreen from "./screens/HeroScreen";
/* the Team screen loads eagerly: its cube art sits half a viewport below
   the fold and a lazy mount raced layout, sometimes staying blank until
   a resize */
import PeopleScreen from "./screens/PeopleScreen";
/* the career globe gets the same treatment as the cube: eager, so its
   canvas never races a lazy mount */
import CareerScreen from "./screens/CareerScreen";
import { useReveal } from "./hooks/useReveal";
import { useSectionSnap } from "./hooks/useSectionSnap";

/* the rest below the fold loads as separate chunks after first paint */
const SolutionsScreen = lazy(() => import("./screens/SolutionsScreen"));
const ContactScreen = lazy(() => import("./screens/ContactScreen"));

ReactGA.initialize("G-61R8F8BGRP");

/* one fixed down-cue for the whole page: always the same spot, always
   steps to the next snapped view; gone at the page end and in the
   free-scrolling stacked band */
function NextCue() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const stackedMq = window.matchMedia(
      "(min-width: 769px) and (max-width: 1024px)"
    );
    let raf = 0;
    const measure = () => {
      raf = 0;
      const max =
        document.documentElement.scrollHeight - window.innerHeight;
      setHidden(stackedMq.matches || window.scrollY >= max - 40);
    };
    /* rAF-throttled: the scrollHeight read forces layout */
    const upd = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", upd, { passive: true });
    window.addEventListener("resize", upd);
    /* the below-fold screens mount lazily after first paint — on a fresh
       load the page is one viewport tall for a moment and the cue would
       wrongly decide it is at the page end; recheck as the document grows */
    const ro = new ResizeObserver(upd);
    ro.observe(document.documentElement);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("scroll", upd);
      window.removeEventListener("resize", upd);
    };
  }, []);
  return (
    <button
      type="button"
      className={`scroll-cue global${hidden ? " hidden" : ""}`}
      aria-label="Scroll to next section"
      onClick={(e) => {
        e.currentTarget.blur();
        window.dispatchEvent(new Event("daalab:step-down"));
      }}
    >
      <span />
      <span />
    </button>
  );
}

export default function App() {
  useReveal();
  useSectionSnap();

  return (
    <>
      <Header />
      <main>
        <HeroScreen />
        <PeopleScreen />
        <Suspense fallback={null}>
          <SolutionsScreen />
        </Suspense>
        <CareerScreen />
        <Suspense fallback={null}>
          <ContactScreen />
        </Suspense>
      </main>
      <NextCue />
    </>
  );
}
